/**
 * Settings Page - Enterprise Admin
 * =================================
 * صفحة الإعدادات العامة - نظام شامل
 */

import React, { useState } from 'react';
import { Card } from '../../../lib/ui/Card';
import { Button } from '../../../lib/ui/Button';
import { Input } from '../../../lib/ui/Input';
import { Dropdown } from '../../../lib/ui/Dropdown';
import { Checkbox } from '../../../lib/ui/Checkbox';
import { useToast } from '../../context/ToastContext';
import { useDialog } from '../../../context/DialogContext';
import { useTheme } from '../../../lib/context/ThemeContext';
import './SettingsPage.css';

// أيقونات
const Icons = {
  settings: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>,
  globe: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>,
  bell: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>,
  database: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></svg>,
  shield: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>,
  mail: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>,
  server: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="8" rx="2" /><rect x="2" y="14" width="20" height="8" rx="2" /><line x1="6" y1="6" x2="6.01" y2="6" /><line x1="6" y1="18" x2="6.01" y2="18" /></svg>,
  save: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>,
  alert: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>,
};

const SettingsPage = () => {
  const toast = useToast();
  const { confirm } = useDialog();
  const { locale, toggleLocale } = useTheme();

  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    // عام
    siteName: 'وترلي',
    siteEmail: 'admin@watrly.com',
    supportEmail: 'support@watrly.com',
    phone: '+966500000000',
    address: 'الرياض، المملكة العربية السعودية',
    timezone: 'Asia/Riyadh',
    currency: 'SAR',
    dateFormat: 'DD/MM/YYYY',
    // إشعارات
    emailNotifications: true,
    smsNotifications: true,
    pushNotifications: true,
    orderNotifications: true,
    systemAlerts: true,
    // أمان
    twoFactorAuth: false,
    sessionTimeout: '30',
    maxLoginAttempts: '5',
    passwordExpiry: '90',
    // نظام
    maintenanceMode: false,
    debugMode: false,
    apiRateLimit: '1000',
    maxFileSize: '10',
  });

  const tabs = [
    { id: 'general', label: 'عام', icon: Icons.settings },
    { id: 'notifications', label: 'الإشعارات', icon: Icons.bell },
    { id: 'security', label: 'الأمان', icon: Icons.shield },
    { id: 'system', label: 'النظام', icon: Icons.server },
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('تم حفظ الإعدادات بنجاح');
    } catch (error) {
      toast.error('فشل حفظ الإعدادات');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (await confirm({ title: 'إعادة الإعدادات', message: 'هل أنت متأكد من إعادة الإعدادات إلى القيم الافتراضية؟', type: 'warning', confirmText: 'إعادة تعيين', cancelText: 'تراجع' })) {
      toast.info('تم إعادة الإعدادات');
    }
  };

  const timezoneOptions = [
    { value: 'Asia/Riyadh', label: 'الرياض (GMT+3)' },
    { value: 'Asia/Dubai', label: 'دبي (GMT+4)' },
    { value: 'Asia/Kuwait', label: 'الكويت (GMT+3)' },
    { value: 'Asia/Bahrain', label: 'البحرين (GMT+3)' },
    { value: 'Asia/Qatar', label: 'قطر (GMT+3)' },
  ];

  const currencyOptions = [
    { value: 'SAR', label: 'ريال سعودي (SAR)' },
    { value: 'AED', label: 'درهم إماراتي (AED)' },
    { value: 'KWD', label: 'دينار كويتي (KWD)' },
    { value: 'BHD', label: 'دينار بحريني (BHD)' },
    { value: 'QAR', label: 'ريال قطري (QAR)' },
  ];

  const dateFormatOptions = [
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
  ];

  const sessionTimeoutOptions = [
    { value: '15', label: '15 دقيقة' },
    { value: '30', label: '30 دقيقة' },
    { value: '60', label: 'ساعة' },
    { value: '120', label: 'ساعتين' },
  ];

  return (
    <div className="watrly-settings">
      {/* Header */}
      <div className="watrly-settings__header">
        <div>
          <h1 className="watrly-settings__title">إعدادات النظام</h1>
          <p className="watrly-settings__subtitle">إدارة إعدادات المنصة والتكوينات العامة</p>
        </div>
        <div className="watrly-settings__header-actions">
          <Button variant="ghost" onClick={handleReset}>
            إعادة تعيين
          </Button>
          <Button variant="primary" onClick={handleSave} loading={saving}>
            {Icons.save}
            حفظ التغييرات
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="watrly-settings__tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`watrly-settings__tab ${activeTab === tab.id ? 'watrly-settings__tab--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* General Tab */}
      {activeTab === 'general' && (
        <>
          <Card className="watrly-settings__card">
            <div className="watrly-settings__card-header">
              <div className="watrly-settings__card-icon">{Icons.settings}</div>
              <div>
                <h3>الإعدادات العامة</h3>
                <p>المعلومات الأساسية للمنصة</p>
              </div>
            </div>
            <div className="watrly-settings__form watrly-settings__form--grid">
              <Input
                label="اسم المنصة"
                value={settings.siteName}
                onChange={(e) => setSettings({ ...settings, siteName: e.target.value })}
              />
              <Input
                label="البريد الإلكتروني الرئيسي"
                type="email"
                value={settings.siteEmail}
                onChange={(e) => setSettings({ ...settings, siteEmail: e.target.value })}
              />
              <Input
                label="بريد الدعم"
                type="email"
                value={settings.supportEmail}
                onChange={(e) => setSettings({ ...settings, supportEmail: e.target.value })}
              />
              <Input
                label="رقم الهاتف"
                value={settings.phone}
                onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
              />
              <div className="watrly-settings__form-full">
                <Input
                  label="العنوان"
                  value={settings.address}
                  onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                />
              </div>
            </div>
          </Card>

          <Card className="watrly-settings__card">
            <div className="watrly-settings__card-header">
              <div className="watrly-settings__card-icon">{Icons.globe}</div>
              <div>
                <h3>التنسيق والمنطقة</h3>
                <p>إعدادات اللغة والمنطقة الزمنية</p>
              </div>
            </div>
            <div className="watrly-settings__form watrly-settings__form--grid">
              <Dropdown
                label="المنطقة الزمنية"
                options={timezoneOptions}
                value={settings.timezone}
                onChange={(val) => setSettings({ ...settings, timezone: val })}
              />
              <Dropdown
                label="العملة"
                options={currencyOptions}
                value={settings.currency}
                onChange={(val) => setSettings({ ...settings, currency: val })}
              />
              <Dropdown
                label="تنسيق التاريخ"
                options={dateFormatOptions}
                value={settings.dateFormat}
                onChange={(val) => setSettings({ ...settings, dateFormat: val })}
              />
              <div className="watrly-settings__language-toggle">
                <span>اللغة الحالية: <strong>{locale === 'ar' ? 'العربية' : 'English'}</strong></span>
                <Button variant="outline" size="sm" onClick={toggleLocale}>
                  تبديل
                </Button>
              </div>
            </div>
          </Card>
        </>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <Card className="watrly-settings__card">
          <div className="watrly-settings__card-header">
            <div className="watrly-settings__card-icon">{Icons.bell}</div>
            <div>
              <h3>إعدادات الإشعارات</h3>
              <p>تحكم في طرق استلام الإشعارات</p>
            </div>
          </div>
          <div className="watrly-settings__notifications-grid">
            <div className="watrly-settings__notification-item">
              <div className="watrly-settings__notification-icon">{Icons.mail}</div>
              <div className="watrly-settings__notification-content">
                <Checkbox
                  label="إشعارات البريد الإلكتروني"
                  description="استلام التنبيهات عبر البريد"
                  checked={settings.emailNotifications}
                  onChange={(e) => setSettings({ ...settings, emailNotifications: e.target.checked })}
                  variant="water-drop"
                />
              </div>
            </div>
            <div className="watrly-settings__notification-item">
              <div className="watrly-settings__notification-icon">{Icons.bell}</div>
              <div className="watrly-settings__notification-content">
                <Checkbox
                  label="إشعارات الرسائل النصية"
                  description="استلام التنبيهات عبر SMS"
                  checked={settings.smsNotifications}
                  onChange={(e) => setSettings({ ...settings, smsNotifications: e.target.checked })}
                  variant="water-drop"
                />
              </div>
            </div>
            <div className="watrly-settings__notification-item">
              <div className="watrly-settings__notification-icon">{Icons.bell}</div>
              <div className="watrly-settings__notification-content">
                <Checkbox
                  label="إشعارات الدفع"
                  description="إشعارات المتصفح والجوال"
                  checked={settings.pushNotifications}
                  onChange={(e) => setSettings({ ...settings, pushNotifications: e.target.checked })}
                  variant="water-drop"
                />
              </div>
            </div>
            <div className="watrly-settings__notification-item">
              <div className="watrly-settings__notification-icon">{Icons.check}</div>
              <div className="watrly-settings__notification-content">
                <Checkbox
                  label="إشعارات الطلبات"
                  description="تنبيهات عند ورود طلبات جديدة"
                  checked={settings.orderNotifications}
                  onChange={(e) => setSettings({ ...settings, orderNotifications: e.target.checked })}
                  variant="water-drop"
                />
              </div>
            </div>
            <div className="watrly-settings__notification-item">
              <div className="watrly-settings__notification-icon">{Icons.alert}</div>
              <div className="watrly-settings__notification-content">
                <Checkbox
                  label="تنبيهات النظام"
                  description="تنبيهات الأخطاء والتحذيرات"
                  checked={settings.systemAlerts}
                  onChange={(e) => setSettings({ ...settings, systemAlerts: e.target.checked })}
                  variant="water-drop"
                />
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <Card className="watrly-settings__card">
          <div className="watrly-settings__card-header">
            <div className="watrly-settings__card-icon">{Icons.shield}</div>
            <div>
              <h3>إعدادات الأمان</h3>
              <p>تكوين سياسات الأمان والحماية</p>
            </div>
          </div>
          <div className="watrly-settings__form watrly-settings__form--grid">
            <div className="watrly-settings__form-full">
              <Checkbox
                label="المصادقة الثنائية (2FA)"
                description="تفعيل المصادقة الثنائية للمديرين"
                checked={settings.twoFactorAuth}
                onChange={(e) => setSettings({ ...settings, twoFactorAuth: e.target.checked })}
                variant="water-drop"
              />
            </div>
            <Dropdown
              label="مهلة انتهاء الجلسة"
              options={sessionTimeoutOptions}
              value={settings.sessionTimeout}
              onChange={(val) => setSettings({ ...settings, sessionTimeout: val })}
            />
            <Input
              label="الحد الأقصى لمحاولات تسجيل الدخول"
              type="number"
              value={settings.maxLoginAttempts}
              onChange={(e) => setSettings({ ...settings, maxLoginAttempts: e.target.value })}
            />
            <Input
              label="صلاحية كلمة المرور (يوم)"
              type="number"
              value={settings.passwordExpiry}
              onChange={(e) => setSettings({ ...settings, passwordExpiry: e.target.value })}
            />
          </div>
        </Card>
      )}

      {/* System Tab */}
      {activeTab === 'system' && (
        <>
          <Card className="watrly-settings__card">
            <div className="watrly-settings__card-header">
              <div className="watrly-settings__card-icon">{Icons.server}</div>
              <div>
                <h3>إعدادات النظام</h3>
                <p>تكوين خيارات النظام المتقدمة</p>
              </div>
            </div>
            <div className="watrly-settings__form watrly-settings__form--grid">
              <div className="watrly-settings__form-full">
                <div className="watrly-settings__warning-box">
                  <Checkbox
                    label="وضع الصيانة"
                    description="تفعيل وضع الصيانة يمنع الوصول للمنصة"
                    checked={settings.maintenanceMode}
                    onChange={(e) => setSettings({ ...settings, maintenanceMode: e.target.checked })}
                    variant="water-drop"
                  />
                </div>
              </div>
              <Checkbox
                label="وضع التصحيح"
                description="تفعيل سجلات التصحيح التفصيلية"
                checked={settings.debugMode}
                onChange={(e) => setSettings({ ...settings, debugMode: e.target.checked })}
                variant="water-drop"
              />
              <Input
                label="حد API (طلب/دقيقة)"
                type="number"
                value={settings.apiRateLimit}
                onChange={(e) => setSettings({ ...settings, apiRateLimit: e.target.value })}
              />
              <Input
                label="الحد الأقصى لحجم الملف (MB)"
                type="number"
                value={settings.maxFileSize}
                onChange={(e) => setSettings({ ...settings, maxFileSize: e.target.value })}
              />
            </div>
          </Card>

          <Card className="watrly-settings__card">
            <div className="watrly-settings__card-header">
              <div className="watrly-settings__card-icon">{Icons.database}</div>
              <div>
                <h3>معلومات قاعدة البيانات</h3>
                <p>حالة الاتصال والإحصائيات</p>
              </div>
            </div>
            <div className="watrly-settings__db-grid">
              <div className="watrly-settings__db-card watrly-settings__db-card--success">
                <span className="watrly-settings__db-label">حالة الاتصال</span>
                <span className="watrly-settings__db-value">متصلة {Icons.check}</span>
              </div>
              <div className="watrly-settings__db-card">
                <span className="watrly-settings__db-label">عدد الجداول</span>
                <span className="watrly-settings__db-value">115</span>
              </div>
              <div className="watrly-settings__db-card">
                <span className="watrly-settings__db-label">الأدوار</span>
                <span className="watrly-settings__db-value">15</span>
              </div>
              <div className="watrly-settings__db-card">
                <span className="watrly-settings__db-label">المستخدمين</span>
                <span className="watrly-settings__db-value">6</span>
              </div>
              <div className="watrly-settings__db-card">
                <span className="watrly-settings__db-label">الشركات</span>
                <span className="watrly-settings__db-value">5</span>
              </div>
              <div className="watrly-settings__db-card">
                <span className="watrly-settings__db-label">إصدار النظام</span>
                <span className="watrly-settings__db-value">1.0.0</span>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default SettingsPage;
